import React, { FC } from 'react';
import { Navigate, useParams, useMatch } from 'react-router-dom';

import {
  ContentApprovalTypes,
  FeedKey,
  GroupFragment,
  SearchContentQueryVariables,
  SearchContentSortFields,
  SortDirections,
  SortMode,
} from 'api';
import { Layouts, Loaders, Queries } from 'components';
import { FeedSelect } from 'features/feed';
import { SetupProfileButton, SetupWorkspaceButton } from 'features/setup';
import { useFollowed, useLocalStorage, useProtectedClient, useCapabilities } from 'hooks';
import { DEFAULT_SORT, FEED_LIMIT } from 'app-constants';
import * as paths from 'paths';

import {
  FeedMenu,
  GroupMembers,
  GroupModeratorTabs,
  GroupPosts,
} from 'features/groups/components/View';

const ViewGroup: FC = () => {
  const client = useProtectedClient();
  const { groups } = useFollowed();
  const { needs } = useCapabilities();
  const params = useParams<{ group_id: string }>();
  const [s, setSort] = useLocalStorage('timelineSort', DEFAULT_SORT);
  const sort = client.sort_mode ? client.sort_mode === SortMode.Trending : s;
  const allowSort = !client.sort_mode;

  const isPosts = !!useMatch(paths.group);
  const isMembers = !!useMatch(paths.groupMembers);
  const isApproval = !!useMatch(paths.groupApproval);
  const isScheduled = !!useMatch(paths.groupScheduled);
  const isRemoved = !!useMatch(paths.groupRemoved);

  const { group_id } = params;
  const isFollowing = groups.some(group => group.id === group_id);

  function handleSort() {
    setSort(!sort);
  }

  function renderMenu() {
    const selectedGroup = groups.find(group => group.id === group_id);

    if (!selectedGroup) {
      return null;
    }

    return (
      <FeedMenu
        enableSort
        selectedGroup={selectedGroup}
        onSort={handleSort}
        sort={sort}
        allowSort={allowSort}
      />
    );
  }

  function canModerate(group: GroupFragment, capability: string) {
    return needs(`Group.View.${capability}`, group.id);
  }

  function renderGroup(group: GroupFragment) {
    const variables = {
      sort,
      key: FeedKey.Group,
      id: group.id,
      limit: FEED_LIMIT,
    };

    const searchContentVariables: SearchContentQueryVariables = {
      search_content: {
        approval: ContentApprovalTypes.Submitted,
        group_list: [group.id],
        sort_by: [
          {
            direction: SortDirections.Desc,
            field: SearchContentSortFields.CreatedAt,
          },
        ],
      },
    };

    if (isPosts) {
      return <GroupPosts variables={variables} isFollowing={isFollowing} group={group} />;
    }

    if (isMembers) {
      return <GroupMembers isFollowing={isFollowing} group={group} />;
    }

    if (canModerate(group, 'Approval') && isApproval) {
      return <GroupModeratorTabs group={group} variables={searchContentVariables} />;
    }

    if (canModerate(group, 'Scheduled') && isScheduled) {
      searchContentVariables.search_content.approval = ContentApprovalTypes.Scheduled;
      return <GroupModeratorTabs group={group} variables={searchContentVariables} />;
    }

    if (canModerate(group, 'Removed') && isRemoved) {
      searchContentVariables.search_content.approval = ContentApprovalTypes.Removed;
      return <GroupModeratorTabs group={group} variables={searchContentVariables} />;
    }

    return <Navigate to={paths.groups} replace />;
  }

  function renderLoader() {
    return (
      <Layouts.AppMain2Column
        hideRightOnMobile
        desktopSplit="8/4"
        left={<Loaders.Widget />}
        right={<Loaders.Widget />}
      />
    );
  }

  return (
    <Layouts.App2Column
      sidebar={
        <FeedSelect menu={renderMenu()}>
          {' '}
          <SetupWorkspaceButton />
          <SetupProfileButton />
        </FeedSelect>
      }
      main={
        <Queries.GetGroup loader={renderLoader()} variables={{ id: group_id }}>
          {group => {
            if (!group) {
              return <Navigate to={paths.groups} replace />;
            }

            return renderGroup(group);
          }}
        </Queries.GetGroup>
      }
    />
  );
};

export default ViewGroup;
